import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography
} from "@material-ui/core";
import { CheckCircle, Refresh } from "@material-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { api } from "../../services/apiService";
import "./notes-to-quiz-claim.scss";

const getErrorMessage = (response) => {
  if (!response) {
    return "We could not prepare this quiz link.";
  }
  if (response.errMsg?.message) {
    return response.errMsg.message;
  }
  if (response.errMsg?.error) {
    return response.errMsg.error;
  }
  if (response.message) {
    return response.message;
  }
  return "This quiz link is unavailable, expired, already used, or assigned to another email address.";
};

const isTokenUsable = (authToken) => {
  if (!authToken) return false;
  try {
    const decodedToken = jwt_decode(authToken);
    return !!decodedToken?.exp && new Date().getTime() < decodedToken.exp * 1000;
  } catch {
    return false;
  }
};

const trackQuizOpen = (token) => {
  Promise.resolve()
    .then(() => api.trackNotesToQuizOpen(token))
    .catch(() => {});
};

// The claim response may carry an optional mentorship consent prompt when the
// ops quiz-generation flow resolved a tutor for this student (design:
// docs/qlearn-mentorship-quizgen-autorequest.md §2 + qlearn-unified PR #122).
// Only a TUTOR-initiated PENDING (or a mutual-activate ACTIVE confirmation) is
// ever sent — a student cannot accept their own request. Anything else, or an
// old backend, omits the field and the claim page behaves byte-identically.
const readMentorshipPrompt = (response) => {
  const prompt = response?.mentorshipPrompt;
  if (!prompt?.linkId) return null;
  return prompt;
};

const tutorName = (prompt) => prompt?.tutorDisplayName || "Your tutor";

export default function NotesToQuizClaimPage() {
  const { token } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  // Set only when the claim succeeds AND carries a mentorship prompt; holding
  // the student on the claim page to show the consent card before the quiz.
  const [quizRoute, setQuizRoute] = useState("");
  const [mentorshipPrompt, setMentorshipPrompt] = useState(null);
  // "pending" (actionable Accept / Not now) | "linked" (confirmation, no
  // buttons) | "dismissed" (Not now — stays PENDING in My Tutors).
  const [cardState, setCardState] = useState("pending");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setErrorMessage("This quiz link is missing its claim token.");
      return;
    }

    trackQuizOpen(token);

    const authToken = window.localStorage.getItem("token");
    if (!isTokenUsable(authToken)) {
      window.localStorage.removeItem("token");
      history.replace(`/login?next=${encodeURIComponent(`/notes-to-quiz/${token}`)}`);
      return;
    }

    let isActive = true;
    setLoading(true);
    setErrorMessage("");

    api.claimNotesToQuiz(token)
      .then((response) => {
        if (!isActive) return;

        if (response?.quizId && response?.quizRoute) {
          const prompt = readMentorshipPrompt(response);
          if (prompt) {
            // Hold on the claim page so the student sees the consent card
            // before entering the quiz; the quiz is one tap away either way.
            setQuizRoute(response.quizRoute);
            setMentorshipPrompt(prompt);
            setCardState(prompt.status === "ACTIVE" ? "linked" : "pending");
            setLoading(false);
            return;
          }
          history.replace(response.quizRoute);
          return;
        }

        setErrorMessage(getErrorMessage(response));
        setLoading(false);
      })
      .catch(() => {
        if (!isActive) return;
        setErrorMessage("We could not prepare this quiz link.");
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [history, token]);

  const retryClaim = () => {
    window.location.reload();
  };

  const startQuiz = () => {
    if (quizRoute) history.replace(quizRoute);
  };

  const acceptMentorship = async () => {
    if (!mentorshipPrompt?.linkId || accepting) return;
    setAccepting(true);
    setAcceptError("");
    try {
      // respondToMentorship RESOLVES {errStatus, errMsg} on failure (it never
      // rejects), so inspect the value rather than relying on catch. Success is
      // the updated ACTIVE DTO. Accept hits the existing PUT /mentorships/{id}/accept.
      const res = await api.respondToMentorship(mentorshipPrompt.linkId, "accept");
      if (res?.errStatus || res?.errMsg) {
        setAcceptError("We couldn't accept that just now. You can accept it anytime in My Tutors.");
      } else {
        setCardState("linked");
      }
    } catch (e) {
      setAcceptError("We couldn't accept that just now. You can accept it anytime in My Tutors.");
    } finally {
      setAccepting(false);
    }
  };

  const dismissMentorship = () => {
    // "Not now" just dismisses — the link stays PENDING and the student can
    // accept or decline it later from My Tutors (per PR #122 §2).
    setCardState("dismissed");
    setAcceptError("");
  };

  if (mentorshipPrompt) {
    const linked = cardState === "linked";
    return (
      <Box component="main" className="notes-to-quiz-page">
        <Paper elevation={0} className="notes-to-quiz-panel">
          <Typography component="h1">Your quiz is ready</Typography>
          <Typography component="p">Accept your tutor's request below, or jump straight in.</Typography>

          {cardState !== "dismissed" && (
            <Box component="section" className="notes-to-quiz-mentorship" data-testid="n2q-mentorship-card">
              {linked ? (
                <>
                  <div className="notes-to-quiz-mentorship-head">
                    <CheckCircle className="notes-to-quiz-mentorship-icon" fontSize="small" />
                    <Typography component="h2" data-testid="n2q-mentorship-linked">
                      You're connected with {tutorName(mentorshipPrompt)}
                    </Typography>
                  </div>
                  <Typography component="p">
                    They can now follow your quiz progress and the questions you tag. You can
                    stop sharing anytime in My Tutors.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="h2">
                    {tutorName(mentorshipPrompt)} would like to follow your QLearn progress
                  </Typography>
                  <Typography component="p">
                    Accepting lets your tutor see your quiz history and the questions you tag,
                    so they can plan your next session. You can stop sharing anytime in My Tutors.
                  </Typography>
                  {acceptError && (
                    <Typography component="p" className="notes-to-quiz-mentorship-error" data-testid="n2q-mentorship-error">
                      {acceptError}
                    </Typography>
                  )}
                  <div className="notes-to-quiz-mentorship-actions">
                    <Button
                      color="primary"
                      variant="contained"
                      size="small"
                      disabled={accepting}
                      onClick={acceptMentorship}
                      data-testid="n2q-mentorship-accept"
                    >
                      {accepting ? <CircularProgress size={18} color="inherit" /> : "Accept"}
                    </Button>
                    <Button
                      color="primary"
                      variant="text"
                      size="small"
                      disabled={accepting}
                      onClick={dismissMentorship}
                      data-testid="n2q-mentorship-decline"
                    >
                      Not now
                    </Button>
                  </div>
                </>
              )}
            </Box>
          )}

          <Box component="div" className="notes-to-quiz-actions">
            <Button
              color="primary"
              variant={cardState === "pending" ? "outlined" : "contained"}
              onClick={startQuiz}
              data-testid="n2q-start-quiz"
            >
              Start quiz
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box component="main" className="notes-to-quiz-page">
      <Paper elevation={0} className="notes-to-quiz-panel">
        {loading ? (
          <>
            <CircularProgress color="primary" />
            <Typography component="h1">Preparing your quiz</Typography>
            <Typography component="p">Opening your approved-bank question set.</Typography>
          </>
        ) : (
          <>
            <Typography component="h1">Quiz link unavailable</Typography>
            <Typography component="p">{errorMessage}</Typography>
            <Box component="div" className="notes-to-quiz-actions">
              <Button
                color="primary"
                variant="contained"
                onClick={() => history.push("/create-quiz")}
              >
                Create quiz
              </Button>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<Refresh />}
                onClick={retryClaim}
              >
                Retry
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
